import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, RotateCcw, XCircle, PlusCircle } from "lucide-react";


// jednotný datumový formát dd.mm.yyyy
function fmt(d) {
  const dd = d.getDate().toString().padStart(2, "0");
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

// sjednocené intervalové formátování dd.mm.yyyy–dd.mm.yyyy
function formatDaysInterval(days) {
  if (!Array.isArray(days) || days.length === 0) return "";

  const sorted = [...days]
    .map((d) => new Date(d.date))
    .sort((a, b) => a - b);

  const intervals = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const cur = sorted[i];
    const diff = Math.round((cur - prev) / 86400000);

    if (diff === 1) {
      prev = cur;
    } else {
      intervals.push([start, prev]);
      start = cur;
      prev = cur;
    }
  }

  intervals.push([start, prev]);

  return intervals
    .map(([s, e]) => {
      if (s.toDateString() === e.toDateString()) {
        return fmt(s);
      }
      return `${fmt(s)}–${fmt(e)}`;
    })
    .join(", ");
}

function formatPharmacy(code) {
  if (!code || code === "FLY") return "Létající";
  return code;
}

function statusColor(status) {
  if (!status) return "blue";

  if (status === "schváleno" || status === "schváleno (oprava)") {
    return "green";
  }
  if (status === "zamítnuto" || status === "zamítnuto (oprava)") {
    return "red";
  }
  if (status === "vráceno") {
    return "orange";
  }
  if (status === "zrušeno") {
    return "gray";
  }
  return "blue";
}



export default function LeavesOverviewPage() {
  const { user, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const isAdmin = user?.role === "admin";
  const isRM = user?.role === "rm";
  const isUser = user?.role === "user";

  // ✔ TADY SPRÁVNĚ – uvnitř komponenty, kde existuje user
  const REQUEST_PAGE_PATH =
    user?.role === "admin" || user?.role === "rm"
      ? "/admin/personal/leaves"
      : "/personal/leaves";

  async function loadRequests() {
    if (!user) return;
    setLoading(true);

    try {
      let collected = [];

      /* ---------------- ADMIN ---------------- */
      if (isAdmin) {
        const rBranches = await fetch(`/api/employees/${user.firma}`, {
          credentials: "include",
        });
        const employees = await rBranches.json();

        const codes = [
          ...new Set(
            employees.map((e) => (e.isFlying ? "FLY" : e.pharmacyCode))
          ),
        ].filter(Boolean);

        for (const code of codes) {
          const r = await fetch(`/api/leaves/${user.firma}/${code}`, {
            credentials: "include",
          });
          const data = await r.json();
          collected = collected.concat(data);
        }
      }

      /* ---------------- RM ---------------- */
      if (isRM) {
        const codes = user.regions || [];
        for (const code of codes) {
          const r = await fetch(`/api/leaves/${user.firma}/${code}`, {
            credentials: "include",
          });
          const data = await r.json();
          collected = collected.concat(data);
        }
      }

      /* ---------------- USER ---------------- */
      if (isUser) {
        const r = await fetch(
          `/api/leaves/${user.firma}/${user.pharmacyCode}`,
          { credentials: "include" }
        );

        const data = await r.json();

        // USER = pobočka → vidí všechny žádosti pobočky
        collected = data;
      }


      collected.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRequests(collected);
    } catch (err) {
      console.error("Error loading leave overview:", err);
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const openInMode = (mode, id) => {
    navigate(`${REQUEST_PAGE_PATH}?mode=${mode}&id=${id}`);
  };

  const cancelPending = async (id) => {
    if (!window.confirm("Opravdu chcete tuto žádost zrušit?")) return;

    try {
      await fetch(`/api/leaves/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "zrušeno" }),
      });
      await loadRequests();
    } catch (err) {
      console.error("Chyba při rušení žádosti:", err);
      alert("Žádost se nepodařilo zrušit.");
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Opravdu smazat tuto žádost?")) return;

    try {
      await fetch(`/api/leaves/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      // okamžitý reload = jistota čistých dat
      window.location.reload();
    } catch (err) {
      alert("Chyba při mazání.");
    }
  };


  if (authLoading) return <div className="p-6">Načítám…</div>;
  if (!user) return <div className="p-6">Nejste přihlášen.</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold mb-4">
        {isUser && "Moje žádosti o volno"}
        {isRM && "Žádosti vašeho regionu"}
        {isAdmin && "Přehled všech žádostí"}
      </h1>

      {loading ? (
        <div>Načítám žádosti…</div>
      ) : (
        <table className="w-full border text-center text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2">Zaměstnanec</th>
              <th className="border p-2">Pobočka</th>
              <th className="border p-2">Dny</th>
              <th className="border p-2">Hodin</th>
              <th className="border p-2">Typ</th>
              <th className="border p-2">Poznámka</th>
              <th className="border p-2">Stav</th>
              <th className="border p-2">Vytvořeno</th>
              <th className="border p-2">Akce</th>
            </tr>
          </thead>

          <tbody>
            {requests.length === 0 && (
              <tr>
                <td colSpan={9} className="p-4 text-gray-500">
                  Žádné žádosti nenalezeny.
                </td>
              </tr>
            )}

            {requests.map((req) => {
              const status = req.status;
              const color = statusColor(status);

              const isWaiting = status === "čeká";
              const isReturned = status === "vráceno";
              const isRejected = status === "zamítnuto";
              const isApproved = status === "schváleno";

              return (
                <tr key={req._id}>
                  <td className="border p-2">{req.employeeName}</td>

                  <td className="border p-2">
                    {formatPharmacy(req.pharmacyCode)}
                  </td>

                  <td className="border p-2">
                    {formatDaysInterval(req.days)}
                  </td>

                  <td className="border p-2">{req.totalHours}</td>

                  <td className="border p-2 whitespace-pre-wrap">
                    {req.metaLabel || "-"}
                  </td>

                  <td className="border p-2 whitespace-pre-wrap">
                    {req.note || "-"}
                  </td>

                  <td
                    className="border p-2 font-semibold"
                    style={{ color }}
                  >
                    {status}
                  </td>

                  <td className="border p-2">
                    {req.createdAt ? fmt(new Date(req.createdAt)) : "-"}
                  </td>

                  <td className="border p-2">
                    <div className="flex flex-nowrap justify-center gap-2 overflow-hidden">

                      {/* === BĚŽNÝ UŽIVATEL === */}
                      {isUser && (
                        <>
                          {isWaiting && (
                            <>
                              {/* Editovat */}
                              <button
                                className="shrink-0 text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => openInMode("edit", req._id)}
                              >
                                <Pencil size={16} /> Upravit
                              </button>

                              {/* Zrušit */}
                              <button
                                className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => deleteRequest(req._id)}
                              >
                                <XCircle size={16} /> Zrušit
                              </button>

                            </>
                          )}

                          {isReturned && (
                            <>
                              <button
                                className="shrink-0 text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => openInMode("edit", req._id)}
                              >
                                <Pencil size={16} /> Upravit
                              </button>

                              <button
                                className="shrink-0 text-green-600 hover:text-green-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => openInMode("resubmit", req._id)}
                              >
                                <RotateCcw size={16} /> Odeslat znovu
                              </button>
                            </>
                          )}

                          {isRejected && (
                            <button
                              className="shrink-0 text-green-600 hover:text-green-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                              onClick={() => openInMode("resubmit", req._id)}
                            >
                              <PlusCircle size={16} /> Nová žádost
                            </button>
                          )}

                          {isApproved && (
                            <>
                              <button
                                className="shrink-0 text-purple-600 hover:text-purple-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => openInMode("change", req._id)}
                              >
                                <Pencil size={16} /> Požádat o opravu
                              </button>

                              <button
                                className="shrink-0 text-orange-600 hover:text-orange-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                                onClick={() => openInMode("cancel", req._id)}
                              >
                                <XCircle size={16} /> Požádat o zrušení
                              </button>
                            </>
                          )}
                        </>
                      )}

                      {/* === ADMIN / RM === */}
                      {(isAdmin || isRM) && (
                        <>
                          <button
                            className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                            onClick={() => openInMode("edit", req._id)}
                          >
                            <Pencil size={16} /> Upravit
                          </button>

                          <button
                            className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-sm whitespace-nowrap"
                            onClick={() => deleteRequest(req._id)}
                          >
                            <Trash2 size={16} /> Smazat
                          </button>
                        </>
                      )}

                    </div>
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
